import { useToast } from "hooks/useToast";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store/store";
import {
  EntityReviewPatch,
  onboardingEntityApi,
  usePatchEntityReviewDetailsMutation,
} from "v2/store/onboardingEntityApi";
import {
  registerEntityReviewFlush,
  unregisterEntityReviewFlush,
} from "v2/utils/entityReviewAutosaveQueue";
import { readError } from "v2/utils/readError";

const AUTOSAVE_DELAY_MS = 800;

export const useEntityReviewAutosave = (
  groupId: string,
  entityId: string
): ((patch: EntityReviewPatch) => void) => {
  const dispatch = useDispatch<AppDispatch>();
  const { alertToast } = useToast();
  const [patchEntityReviewDetails] = usePatchEntityReviewDetailsMutation();

  const pendingRef = useRef<EntityReviewPatch>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushRef = useRef<(() => Promise<void>) | null>(null);

  const clearPendingTimer = useCallback(() => {
    if (!timerRef.current) return;

    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const flush = useCallback(async () => {
    clearPendingTimer();

    const patch = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(patch).length === 0) return;

    try {
      await patchEntityReviewDetails({ entityId, groupId, patch }).unwrap();
    } catch (error) {
      pendingRef.current = { ...patch, ...pendingRef.current };
      throw error;
    }
  }, [entityId, groupId, patchEntityReviewDetails, clearPendingTimer]);

  const flushWithToast = useCallback(async () => {
    try {
      await flush();
    } catch (error) {
      alertToast({ message: readError(error) });
    }
  }, [flush, alertToast]);

  useEffect(() => {
    flushRef.current = flushWithToast;
    registerEntityReviewFlush(entityId, flush);

    return () => {
      unregisterEntityReviewFlush(entityId);
    };
  }, [entityId, flush, flushWithToast]);

  useEffect(
    () => () => {
      void flushRef.current?.();
    },
    []
  );

  return useCallback(
    (patch: EntityReviewPatch) => {
      dispatch(
        onboardingEntityApi.util.updateQueryData(
          "getGroupReviewDetails",
          { groupId },
          (draft) => {
            const entity = draft.find((item) => item.entityId === entityId);
            if (!entity) return;

            Object.assign(entity, patch);
            if (patch.states) {
              entity.homeStateId =
                patch.states.find((state) => state.isHome)?.id ?? null;
            }
          }
        )
      );

      pendingRef.current = { ...pendingRef.current, ...patch };
      clearPendingTimer();
      timerRef.current = setTimeout(
        () => void flushWithToast(),
        AUTOSAVE_DELAY_MS
      );
    },
    [dispatch, groupId, entityId, flushWithToast, clearPendingTimer]
  );
};
